// SEQ-180 phase 2, task P13: config to component.
//
// TWO RENDERING PATHS, ONE DASHBOARD. The five phase 1 chart widgets keep the
// components they already have, because each carries interactive behaviour
// the widget config has no field for: the scatter's axis toggle, the trend
// line's project selector, the table's column visibility and its CSV and XLSX
// exports. Reproducing them from a generic renderer would quietly drop
// features, so the renderer recognises them by `config.widgetId` and hands
// them their existing component. Everything else, including every widget a
// user builds, goes through the generic charts in
// PnlWidgetGenericCharts.tsx.
//
// That boundary is about the RENDERING PATH, and it still holds: nothing here
// reproduces a phase 1 widget. It is not a claim that the files are never
// edited. The 12px type floor (14 September 2026) did change axis tick sizes
// inside PnlMonthlyTrendChart, PnlProfitByProjectBar and PnlProfitScatterCharts,
// because a floor applied to half a page is a defect rather than a boundary.
// Those edits are tick font sizes and nothing else.
//
// `config.widgetId` is preserved when a dashboard is duplicated
// (usePnlDashboards.ts), which is what makes "Save a copy" of the default
// dashboard look like the thing it copied, exactly as plan decision D11
// requires.
//
// An unknown chart type renders a named fallback card, never a crash: a
// config can arrive from a jsonb column written by a newer version of the
// app, and a blank dashboard tells the user nothing.
import { useMemo, type ReactElement, type ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  AlertTriangle,
  BarChart3,
  ChartScatter as ScatterChartIcon,
  Clock,
  DollarSign,
  HelpCircle,
  Layers,
  LineChart as LineChartIcon,
  RefreshCw,
  Table2,
  TrendingDown,
  TrendingUp,
  Wallet,
  Wrench,
} from "lucide-react";
import type { CSSProperties } from "react";
import type { PnlChartType, PnlWidgetConfig } from "../../lib/pnl/pnlWidgetSchema";
import { PNL_METRIC_KEYS, type PnlMetricDefinition, type PnlUnit } from "../../lib/pnl/pnlAnalytics";
import { formatMetricValue } from "../../lib/pnl/formatMetricValue";
import { formatPnlMetric, pnlChartCardStyle, pnlChartHeaderStyle } from "../../features/pnlAnalytics/pnlAnalyticsFormat";
import { RAW_WAGES_NOTE, showsRawWagesNote } from "../../lib/pnl/wagesMultiplierGrain";
import { DRILL_DOWN_HINT, groupingCaption, offersDrillDown } from "../../features/pnlAnalytics/groupByVocabulary";
import { PNL_ENTRANCE_SPRING } from "../../features/pnlAnalytics/pnlMotion";
import type { PnlMetricOption } from "../../features/pnlAnalytics/usePnlMetricRegistry";
import type { PnlKeyView } from "../../features/pnlAnalytics/widgetDataset";
import type { PnlAnalyticsQueryFilters } from "../../features/pnlAnalytics/usePnlAnalytics";
import {
  COMPARISON_CAPTIONS,
  computeDelta,
  deltaSentiment,
  FOLD_REFUSAL_MESSAGES,
  resolveWidgetValue,
} from "../../features/pnlAnalytics/widgetPresentation";
import { validateDraft } from "../../features/pnlAnalytics/widgetEditorState";
import { PnlKpiCard, type PnlKpiDelta } from "./PnlKpiCard";
import { PnlProfitByProjectBar } from "./PnlProfitByProjectBar";
import { PnlProfitVsOvertimeScatter, PnlProfitVsPatchingScatter } from "./PnlProfitScatterCharts";
import { PnlProjectPerformanceTable } from "./PnlProjectPerformanceTable";
import { PnlMonthlyTrendChart } from "./PnlMonthlyTrendChart";
import {
  PnlWidgetBarChart,
  PnlWidgetLineChart,
  PnlWidgetScatterChart,
  PnlWidgetStackedBarChart,
  PnlWidgetTable,
} from "./PnlWidgetGenericCharts";
import { PnlMonthOnMonthTable } from "./PnlMonthOnMonthTable";
import { PnlGridWidget } from "./PnlGridWidget";
import { mappedOnlyOvertimePctRevenue } from "../../features/pnlAnalytics/mappedOnly";
import { PHASE1_WIDGET_IDS, toWidgetBuckets, type WidgetBucket } from "../../features/pnlAnalytics/widgetBuckets";
import { HowWorkedOut } from "../howWorkedOut/HowWorkedOut";
import type { MetricDefinition, RightNowValue } from "../howWorkedOut/metricDefinition";
import {
  describeWidget,
  monthOnMonthRowContext,
  payrollFreshness,
  PNL_SPECIAL_WIDGET_FIGURES,
  SPECIAL_WIDGETS_USING_PAYROLL,
} from "../../features/pnlAnalytics/pnlWidgetDescriptions";
import { describeGrid } from "../../features/pnlAnalytics/gridDescriptions";
import type { PnlProjectNameSource } from "../../features/pnlAnalytics/widgetDataset";

interface KpiPresentation {
  colour: string | "profit";
  icon: (profitPositive: boolean) => ReactElement;
}

// The eight phase 1 cards' icons and colours, keyed by metric so a user's own
// KPI on the same metric looks the same as the default dashboard's.
const KPI_PRESENTATION: Record<string, KpiPresentation> = {
  "calculated.total_income": { colour: "#3b82f6", icon: () => <DollarSign size={16} /> },
  "calculated.net_profit": {
    colour: "profit",
    icon: (positive) => (positive ? <TrendingUp size={16} /> : <TrendingDown size={16} />),
  },
  "calculated.profit_percentage": { colour: "profit", icon: () => <TrendingUp size={16} /> },
  "calculated.total_cost": { colour: "#ef4444", icon: () => <Wallet size={16} /> },
  "labour.overtime": { colour: "#f59e0b", icon: () => <Clock size={16} /> },
  "calculated.overtime_pct_revenue": { colour: "#f59e0b", icon: () => <Clock size={16} /> },
  "expense.patching": { colour: "#8b5cf6", icon: () => <Wrench size={16} /> },
  "calculated.patching_pct_revenue": { colour: "#8b5cf6", icon: () => <Wrench size={16} /> },
};

const DEFAULT_KPI_PRESENTATION: KpiPresentation = { colour: "var(--seq-blue)", icon: () => <BarChart3 size={16} /> };

/**
 * Design audit, MEDIUM: every generic widget wore the same BarChart3 in the
 * page title's purple, which said nothing about the widget and borrowed the
 * heading's colour for a card. The icon now names the chart type, in the
 * brand blue, so a glance down a dashboard tells you what each card is.
 */
function chartTypeIcon(chartType: PnlChartType) {
  const style = { color: "var(--seq-blue)" } as const;
  switch (chartType) {
    case "line":
      return <LineChartIcon size={18} style={style} aria-hidden="true" />;
    case "scatter":
      return <ScatterChartIcon size={18} style={style} aria-hidden="true" />;
    case "table":
    case "month_on_month":
    case "grid":
      return <Table2 size={18} style={style} aria-hidden="true" />;
    case "stacked_bar":
      return <Layers size={18} style={style} aria-hidden="true" />;
    default:
      return <BarChart3 size={18} style={style} aria-hidden="true" />;
  }
}

export interface PnlWidgetRendererProps {
  config: PnlWidgetConfig;
  view: PnlKeyView;
  comparisonView: PnlKeyView | null;
  metrics: PnlMetricOption[];
  /** The page's live filters, needed by the trend widget's own on-demand project call. */
  queryFilters: PnlAnalyticsQueryFilters;
  /**
   * SEQ-193. Every project's display name, needed only by a month_on_month
   * widget's "group by project" mode (its row labels). Optional: the editor's
   * live preview does not thread this through, and a project id shows as
   * itself rather than a name in that one preview, which is a cosmetic
   * degradation, not a functional one.
   */
  projects?: PnlProjectNameSource[];
  /** Set in view mode only; drilling from inside the editor's live preview would be confusing. */
  onDrillDown?: (bucket: WidgetBucket, config: PnlWidgetConfig) => void;
  /**
   * React review, HIGH: the message from THIS widget's own request key, when
   * that key failed. A dashboard makes up to eight independent calls, so a
   * widget whose key came back fine keeps rendering while its neighbour says
   * what went wrong.
   */
  loadError?: string | null;
  /** Retries every key of the current render. Offered next to a widget's own error. */
  onRetry?: () => void;
  /** SEQ-240: dataset.meta.payrollAsAt, shown as Last updated on figures that use payroll. */
  payrollAsAt?: string | null;
  /** SEQ-199. Bumped by the page Refresh, so a grid reloads its own project_month call too. */
  refreshTick?: number;
}

/** A built-in widget's pop-up, with the payroll refresh time when it shows a payroll figure. */
function specialWidgetInfo(widgetId: string, definition: MetricDefinition, payrollAsAt: string | null | undefined) {
  const lastUpdated = SPECIAL_WIDGETS_USING_PAYROLL.has(widgetId) ? payrollFreshness(payrollAsAt) : undefined;
  return <HowWorkedOut definition={{ ...definition, lastUpdated }} />;
}

function MessageCard({ title, icon, children }: { title: string; icon: ReactElement; children: ReactNode }) {
  return (
    <div style={pnlChartCardStyle} className="pnl-chart-card">
      <div style={pnlChartHeaderStyle}>
        {icon}
        {title}
      </div>
      <div style={{ color: "var(--text-secondary)", fontSize: "0.85rem", lineHeight: 1.6 }}>{children}</div>
    </div>
  );
}

/*
 * The raw-wages note (SEQ-180 follow-up). Sits under the chart body rather
 * than in the page footer, because the reader who is about to total a column
 * is looking at the column, not at the foot of the page.
 */
const rawWagesNoteStyle: CSSProperties = {
  // 0.75rem is this page's 12px floor. The note explains why a column does not
  // sum, so it is the last thing that should be set below it.
  fontSize: "0.75rem",
  color: "var(--text-muted)",
  margin: "8px 0 0",
  lineHeight: 1.4,
};

/*
 * The grouping caption, under the widget's title.
 *
 * The editor's Group by hint only ever reaches the person who BUILT the
 * widget. Most people on this page opened a dashboard someone else made, so
 * the widget itself has to say how it is grouped, in the place they are
 * already looking. Quiet on purpose: it is context for the title, not a
 * second title.
 */
const groupingCaptionStyle: CSSProperties = {
  fontSize: "0.75rem",
  color: "var(--text-muted)",
  margin: "0 0 12px",
  lineHeight: 1.4,
};

export function PnlWidgetRenderer(props: PnlWidgetRendererProps) {
  const { config, metrics, loadError, onRetry } = props;
  const reduce = useReducedMotion() ?? false;

  // A SOFT issue (today: a comparison on a lifetime range) leaves the config
  // valid and readable. The widget renders, the section 7.3 message is shown
  // above it in the same wording the editor uses, and the widget runs without
  // the part it cannot honour. Dropping it instead would take a card off the
  // dashboard with nothing said (team lead decision on the P8 report).
  const softIssues = useMemo(() => validateDraft(config, metrics).softIssues, [config, metrics]);

  // This widget's own request key failed. Its neighbours on a key that
  // succeeded are still rendering their data, so this card says what went
  // wrong here rather than the page blanking the whole grid.
  if (loadError) {
    return (
      <MessageCard title={config.name} icon={<AlertTriangle size={18} style={{ color: "#f59e0b" }} />}>
        <p style={{ margin: "0 0 10px" }}>This widget could not load its data. {loadError}</p>
        {onRetry && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={onRetry}>
            <RefreshCw size={13} aria-hidden="true" /> Retry
          </button>
        )}
      </MessageCard>
    );
  }

  const body = <PnlWidgetBody {...props} />;

  if (softIssues.length === 0) return body;

  return (
    <div style={{ display: "grid", gridTemplateRows: "auto 1fr", gap: 6, minHeight: 0 }}>
      <motion.p
        role="status"
        initial={reduce ? false : { opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={PNL_ENTRANCE_SPRING}
        style={{
          margin: 0,
          padding: "6px 10px",
          borderRadius: "var(--border-radius-sm)",
          border: "1px solid rgba(239, 68, 68, 0.3)",
          background: "#fef2f2",
          color: "#b91c1c",
          fontSize: 12,
          lineHeight: 1.5,
        }}
      >
        {softIssues.map((issue) => issue.message).join(" ")}
      </motion.p>
      {body}
    </div>
  );
}

function PnlWidgetBody({ config, view, comparisonView, metrics, queryFilters, onDrillDown, projects, payrollAsAt, refreshTick }: PnlWidgetRendererProps) {
  const registryByKey = useMemo(() => new Map(metrics.map((m) => [m.metricKey, m])), [metrics]);
  const unitByMetric = useMemo(() => {
    const lookup: Record<string, PnlUnit> = {};
    for (const metric of metrics) lookup[metric.metricKey] = metric.unit ?? "currency";
    return lookup;
  }, [metrics]);

  const buckets = useMemo(
    () => toWidgetBuckets(config, view.rows, comparisonView?.rows ?? null),
    [config, view.rows, comparisonView],
  );

  // A metric deleted from the registry must never render a blank card: name
  // the key so the user can fix it in the editor (plan section 7.1).
  //
  // SEQ-193: a month_on_month widget in "company" mode can name several
  // metrics via `metrics`, so `config.metric` alone (kept only as the
  // fallback/primary) is not the right thing to gate on here.
  // PnlMonthOnMonthTable checks every one of its own metrics itself and
  // renders the same kind of message, per row mode.
  //
  // SEQ-199: a grid reads measures by rollup field, never `metric`, so a
  // registry change cannot strand it either.
  if (config.chartType !== "month_on_month" && config.chartType !== "grid" && !registryByKey.has(config.metric) && metrics.length > 0) {
    return (
      <MessageCard title={config.name} icon={<AlertTriangle size={18} style={{ color: "#f59e0b" }} />}>
        This widget uses <strong>{config.metric}</strong>, which no longer exists in the metric registry. Edit the
        widget and pick a replacement metric.
      </MessageCard>
    );
  }

  if (PHASE1_WIDGET_IDS.has(config.widgetId)) {
    switch (config.widgetId) {
      case "bar-profit-percent-by-project":
        return (
          <PnlProfitByProjectBar
            projects={view.projectRows}
            info={specialWidgetInfo(config.widgetId, PNL_SPECIAL_WIDGET_FIGURES["bar-profit-percent-by-project"], payrollAsAt)}
          />
        );
      case "scatter-profit-vs-overtime":
        return (
          <PnlProfitVsOvertimeScatter
            projects={view.projectRows}
            info={specialWidgetInfo(config.widgetId, PNL_SPECIAL_WIDGET_FIGURES["scatter-profit-vs-overtime"], payrollAsAt)}
          />
        );
      case "scatter-profit-vs-patching":
        return (
          <PnlProfitVsPatchingScatter
            projects={view.projectRows}
            info={specialWidgetInfo(config.widgetId, PNL_SPECIAL_WIDGET_FIGURES["scatter-profit-vs-patching"], payrollAsAt)}
          />
        );
      case "table-project-performance":
        return (
          <PnlProjectPerformanceTable
            rows={view.projectRows}
            queryFilters={queryFilters}
            metrics={metrics}
            payrollAsAt={payrollAsAt}
            info={specialWidgetInfo(config.widgetId, PNL_SPECIAL_WIDGET_FIGURES["table-project-performance"], payrollAsAt)}
          />
        );
      case "line-monthly-trend":
        return (
          <PnlMonthlyTrendChart
            info={specialWidgetInfo(config.widgetId, PNL_SPECIAL_WIDGET_FIGURES["line-monthly-trend"], payrollAsAt)}
            filters={queryFilters}
            companyByMonth={view.monthRows}
            projectOptions={view.projectRows.map((p) => ({ id: p.projectId, name: p.projectName }))}
          />
        );
      default:
        break;
    }
  }

  if (config.chartType === "KPI") {
    return (
      <PnlWidgetKpiCard
        config={config}
        view={view}
        comparisonView={comparisonView}
        unitByMetric={unitByMetric}
        metricDef={registryByKey.get(config.metric)}
        definition={describeWidget(config, metrics, { payrollAsAt })}
      />
    );
  }

  const unit = unitByMetric[config.metric] ?? "currency";
  const secondaryUnit = config.secondaryMetric ? (unitByMetric[config.secondaryMetric] ?? "currency") : unit;
  const metricName = registryByKey.get(config.metric)?.displayName ?? config.metric;
  const secondaryName = config.secondaryMetric
    ? (registryByKey.get(config.secondaryMetric)?.displayName ?? config.secondaryMetric)
    : "";
  // Named only when a comparison was actually FETCHED. A lifetime range
  // resolves to no prior period, so the request plan issues no comparison
  // call and there is no second series to label (dashboardRequestKeys.ts).
  const comparisonName =
    config.comparison && comparisonView !== null ? (COMPARISON_CAPTIONS[config.comparison.kind] ?? "Comparison") : null;

  const chartProps = {
    config,
    buckets,
    unit,
    secondaryUnit,
    metricName,
    secondaryName,
    comparisonName,
    onBucketClick: onDrillDown ? (bucket: WidgetBucket) => onDrillDown(bucket, config) : undefined,
  };

  const body = (() => {
    switch (config.chartType) {
      case "bar":
        return <PnlWidgetBarChart {...chartProps} />;
      case "stacked_bar":
        return <PnlWidgetStackedBarChart {...chartProps} />;
      case "line":
        return <PnlWidgetLineChart {...chartProps} />;
      case "scatter":
        return <PnlWidgetScatterChart {...chartProps} />;
      case "table":
        return <PnlWidgetTable {...chartProps} />;
      case "month_on_month":
        return (
          <PnlMonthOnMonthTable
            config={config}
            view={view}
            metrics={metrics}
            queryFilters={queryFilters}
            projects={projects}
            describeContext={monthOnMonthRowContext(config)}
            describeWhere={`in ${config.name}`}
          />
        );
      case "grid":
        return <PnlGridWidget config={config} view={view} queryFilters={queryFilters} refreshTick={refreshTick} payrollAsAt={payrollAsAt} />;
      default:
        return null;
    }
  })();

  if (body === null) {
    return (
      <MessageCard title={config.name} icon={<HelpCircle size={18} style={{ color: "var(--text-secondary)" }} />}>
        This widget asks for a <strong>{String(config.chartType)}</strong> chart, which this version of the page cannot
        draw. Edit the widget and choose another chart type.
      </MessageCard>
    );
  }

  const caption = groupingCaption(config.chartType, config.groupBy);
  // Said only where a click DOES something: the line chart draws no clickable
  // mark, and the editor's live preview passes no drill handler at all.
  const drillHint = onDrillDown && offersDrillDown(config.chartType) ? DRILL_DOWN_HINT[config.chartType] : null;

  return (
    <div style={pnlChartCardStyle} className="pnl-chart-card">
      {/* SEQ-199: the grid card title sits on the type scale (16px); the shared 0.95rem header is 15.2px, off it. */}
      <div style={{ ...pnlChartHeaderStyle, ...(config.chartType === "grid" ? { fontSize: "1rem" } : {}), marginBottom: caption ? 2 : pnlChartHeaderStyle.marginBottom }}>
        {chartTypeIcon(config.chartType)}
        {config.name}
        <HowWorkedOut
          definition={config.chartType === "grid" ? describeGrid(config, { payrollAsAt }) : describeWidget(config, metrics, { payrollAsAt })}
        />
      </div>
      {caption && (
        <p style={groupingCaptionStyle}>
          {caption}
          {drillHint && <span className="pnl-widget-drill-hint"> {drillHint}</span>}
        </p>
      )}
      {body}
      {showsRawWagesNote(config.groupBy, config.metric) && (
        <p style={rawWagesNoteStyle}>{RAW_WAGES_NOTE}</p>
      )}
    </div>
  );
}

interface PnlWidgetKpiCardProps {
  config: PnlWidgetConfig;
  view: PnlKeyView;
  comparisonView: PnlKeyView | null;
  unitByMetric: Record<string, PnlUnit>;
  /** This widget's own registry row, which is what decides whether a rise is good news. */
  metricDef: PnlMetricDefinition | undefined;
  /** SEQ-240: the card's "How is this worked out" text. */
  definition: MetricDefinition;
}

/** The unit the Right now row formats with: the card's own display unit, so it masks exactly as the card does. */
function rightNowUnit(resolved: { displayUnit: string | null; unit: PnlUnit | null }): PnlUnit {
  if (resolved.displayUnit === "compact-currency" || resolved.displayUnit === "currency") return "currency";
  if (resolved.displayUnit === "percent") return "percent";
  if (resolved.displayUnit === "hours") return "hours";
  return resolved.unit ?? "currency";
}

function PnlWidgetKpiCard({ config, view, comparisonView, unitByMetric, metricDef, definition }: PnlWidgetKpiCardProps) {
  const resolved = resolveWidgetValue(config, view.companyTotals, view.rows, unitByMetric);
  const profitPositive = view.companyTotals.profit >= 0;
  const presentation = KPI_PRESENTATION[config.metric] ?? DEFAULT_KPI_PRESENTATION;
  const colour = presentation.colour === "profit" ? (profitPositive ? "#10b981" : "#ef4444") : presentation.colour;

  // ONE formatter, used for the settled string and for every frame of the
  // count-up. Two would be how a card ends up counting in plain numbers and
  // landing on a currency.
  const formatValue = (input: number | null) =>
    resolved.displayUnit !== null
      ? formatPnlMetric(input, resolved.displayUnit)
      : formatMetricValue(input, resolved.unit ?? "currency");
  const value = formatValue(resolved.value);
  // H2/H3: a refused fold renders the placeholder with the reason on the
  // card, never a re-folded number.
  const refusalNote = resolved.refusal ? FOLD_REFUSAL_MESSAGES[resolved.refusal] : null;

  let delta: PnlKpiDelta | undefined;
  if (config.comparison && comparisonView) {
    const prior = resolveWidgetValue(config, comparisonView.companyTotals, comparisonView.rows, unitByMetric);
    const change = computeDelta(resolved.value, prior.value);
    if (change) {
      delta = {
        label:
          resolved.displayUnit !== null
            ? formatPnlMetric(Math.abs(change.change), resolved.displayUnit)
            : formatMetricValue(Math.abs(change.change), resolved.unit ?? "currency"),
        direction: change.direction,
        // Colour follows the registry's own view of the metric, never the
        // sign: overtime cost rising is bad news with an up arrow.
        sentiment: deltaSentiment(change.direction, metricDef),
        caption: COMPARISON_CAPTIONS[config.comparison.kind] ?? "vs prior period",
      };
    }
  }

  const rightNow: RightNowValue[] = [{ label: config.name, value: resolved.value, unit: rightNowUnit(resolved) }];
  // SEQ-240 audit B, row 7: the card divides by every project's income, so the
  // pop-up also shows the same ratio over projects with a payroll mapping.
  if (config.metric === PNL_METRIC_KEYS.calculated.overtimePctRevenue && view.projectRows.length > 0) {
    const mappedOnly = mappedOnlyOvertimePctRevenue(view.projectRows);
    if (mappedOnly.unmappedCount > 0) {
      rightNow.push({ label: "Projects with payroll mapped only", value: mappedOnly.pct, unit: "percent" });
    }
  }

  return (
    <div>
      <PnlKpiCard
        info={<HowWorkedOut definition={definition} rightNow={rightNow} />}
        label={config.name}
        value={value}
        icon={presentation.icon(profitPositive)}
        color={colour}
        delta={delta}
        countTo={resolved.value}
        formatValue={formatValue}
      />
      {refusalNote && <p className="pnl-widget-note" role="note">{refusalNote}</p>}
    </div>
  );
}
